import { useState, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import type { WarehouseStrategy } from '@/lib/api/controlPlaneClient'
import { useAvailableWarehouses } from './useAvailableWarehouses'

interface StockSourcesEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentStrategy: WarehouseStrategy | null
  currentWarehouses: string[]
  onSave: (strategy: WarehouseStrategy, warehouses: string[]) => Promise<void>
  isSaving: boolean
}

export function StockSourcesEditorModal({
  open,
  onOpenChange,
  currentStrategy,
  currentWarehouses,
  onSave,
  isSaving,
}: StockSourcesEditorModalProps) {
  const [strategy, setStrategy] = useState<WarehouseStrategy>(
    currentStrategy ?? 'all',
  )
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>(
    currentWarehouses,
  )
  const [saveError, setSaveError] = useState<string | null>(null)
  const prevOpen = useRef(false)

  const { warehouses, isLoading, isError } = useAvailableWarehouses(open)

  // Reset state when modal opens
  useEffect(() => {
    if (open && !prevOpen.current) {
      setStrategy(currentStrategy ?? 'all')
      setSelectedWarehouses(currentWarehouses)
      setSaveError(null)
    }
    prevOpen.current = open
  }, [open, currentStrategy, currentWarehouses])

  const hasChanged =
    strategy !== currentStrategy ||
    JSON.stringify([...selectedWarehouses].sort()) !==
      JSON.stringify([...currentWarehouses].sort())

  const canSave = hasChanged && !isSaving

  const toggleWarehouse = (name: string, checked: boolean) => {
    setSelectedWarehouses((prev) =>
      checked ? [...prev, name] : prev.filter((w) => w !== name),
    )
  }

  const handleSave = async () => {
    setSaveError(null)
    try {
      await onSave(strategy, selectedWarehouses)
    } catch {
      setSaveError('Failed to save. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader className="pl-12">
          <DialogTitle>Stock Sources</DialogTitle>
          <DialogDescription>
            Choose which warehouses determine stock availability.
          </DialogDescription>
        </DialogHeader>

        {saveError && (
          <div className="mx-6 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
            <p className="text-sm text-red-600 dark:text-red-400">
              {saveError}
            </p>
          </div>
        )}

        <div className="px-6 space-y-4">
          {/* Radio group — native HTML radios */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="warehouse-strategy"
                checked={strategy === 'all'}
                onChange={() => setStrategy('all')}
                className="mt-1 h-4 w-4 accent-primary"
              />
              <div>
                <span className="text-sm font-medium">All Warehouses</span>
                <p className="text-xs text-muted-foreground">
                  Stock from all warehouses combined
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="warehouse-strategy"
                checked={strategy === 'selected'}
                onChange={() => setStrategy('selected')}
                className="mt-1 h-4 w-4 accent-primary"
              />
              <div>
                <span className="text-sm font-medium">
                  Selected Warehouses
                </span>
                <p className="text-xs text-muted-foreground">
                  Stock from specific warehouses only
                </p>
              </div>
            </label>
          </div>

          {/* Conditional warehouse checkbox list */}
          {strategy === 'selected' && (
            <div className="max-h-[240px] overflow-y-auto rounded-sm border border-border p-3 space-y-2">
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="w-full h-6 rounded-md" />
                  <Skeleton className="w-3/4 h-6 rounded-md" />
                  <Skeleton className="w-5/6 h-6 rounded-md" />
                </div>
              ) : isError ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Failed to load warehouses. Please close and try again.
                </p>
              ) : warehouses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No warehouses found.
                </p>
              ) : (
                warehouses.map((w) => {
                  const id = `warehouse-${w.name}`
                  return (
                    <div key={w.name} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={selectedWarehouses.includes(w.name)}
                        onCheckedChange={(checked) =>
                          toggleWarehouse(w.name, !!checked)
                        }
                      />
                      <label
                        htmlFor={id}
                        className="text-sm cursor-pointer select-none"
                      >
                        {w.name}
                      </label>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="min-w-[80px]"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
